import {
	Box,
	Button,
	Inline,
	Icons,
	Stack,
	Heading,
	Col,
	Divider,
	Grid,
	joinAttributes,
	Text,
} from "@sembark-travel/ui/base"
import { Dialog } from "@sembark-travel/ui/dialog"
import { Markdown } from "@sembark-travel/ui/markdown"
import {
	clone,
	formatDate,
	humanizeDuration,
	setUnit,
} from "@sembark-travel/datetime-utils"
import { useXHR } from "@sembark-travel/xhr"
import {
	Form,
	TextInputField,
	MarkdownInputField,
	withServerErrors,
	SubmissionError,
	validateFormValues,
	SelectField,
	FormSpy,
	EmptyNumberValidator,
	DatePickerField,
	CheckboxInputField,
	GetFieldValue,
	FieldArray,
	arrayMutators,
} from "@sembark-travel/ui/form"
import { Optional } from "utility-types"
import * as Validator from "yup"
import { SelectTripDestination } from "../TripDestinations"
import { useAuthUser } from "../Auth"
import { useMemo } from "react"
import config from "../config"
import { SelectTransportServiceLocationPoints } from "./Points"
import { ITransportService, NewServiceCredentials } from "./store"

const INITIAL_VALUES: NewServiceCredentials = {
	from: undefined,
	to: undefined,
	short_code: "",
	trip_destinations: [],
	transport_service: [
		{
			service: "",
			service_short_name: "",
			distance: 0,
			start_time: undefined,
			duration: undefined,
			service_details: "",
		},
	],
}

export function NewServicesForm({
	initialValues: propInitialValues,
	onSuccess,
	onCancel,
	type,
	serviceLocationId,
	canAddMoreServices,
	url,
}: {
	initialValues?: Optional<NewServiceCredentials>
	onCancel?: () => void
	onSuccess: (transportService: ITransportService) => void
	type: "EditSingleService" | "addLocationService"
	serviceLocationId: number | undefined
	canAddMoreServices: boolean
	url: string
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const can_attach_services_to_destinations = Boolean(
		user?.tenant?.feature_flags?.attach_services_to_destinations
	)
	const validationSchema = validateFormValues(
		Validator.object().shape({
			from: Validator.object().required(
				"Please provide the pickup / start location of service."
			),
			to: Validator.object().nullable(),
			transport_service: Validator.array()
				.of(
					Validator.object({
						service: Validator.string()
							.required("Please provide the name or type of the service.")
							.max(
								191,
								"Please keep the service name within 191 characters for better readability."
							),

						service_short_name: Validator.string()
							.nullable()
							.max(191, "Please use 191 or fewer characters for code name."),

						distance: EmptyNumberValidator()
							.typeError("Distance should be a number")
							.integer("Distance should be an integer")
							.min(0, "Please use a non negative number"),

						duration: EmptyNumberValidator()
							.typeError("Duration should be a number")
							.integer("Duration should be an integer"),

						service_details: Validator.string()
							.nullable()
							.max(10000, "Please use less than 10000 characters"),
					})
				)
				.test("unique-service-fields", "", function (items) {
					if (!items) return true

					const { path, createError } = this
					const errors:
						| Validator.ValidationError
						| Validator.ValidationError[] = []

					const serviceMap = new Map() // service → first index
					const duplicates: {
						index: number
						service: string
						code: string
						serviceDuplicate: boolean | ""
					}[] = []

					items.forEach((row, index) => {
						const service = row.service?.trim().toLowerCase() || ""
						const code = row.service_short_name?.trim().toLowerCase() || ""

						const serviceDuplicate = service && serviceMap.has(service + code)

						if (serviceDuplicate) {
							duplicates.push({
								index,
								service,
								code,
								serviceDuplicate,
							})
						}

						// Register first occurrence
						if (service && !serviceDuplicate)
							serviceMap.set(service + code, index)
					})

					duplicates.forEach((dup) => {
						const firstServiceIndex = serviceMap.get(dup.service + dup.code)

						if (dup.serviceDuplicate && dup.service) {
							errors.push(
								createError({
									path: `${path}[${dup.index}].service`,
									message:
										"Service Name already exists. Change Service Name or Service Code.",
								})
							)
							if (dup.code) {
								errors.push(
									createError({
										path: `${path}[${dup.index}].service_short_name`,
										message:
											"Service Code already exists. Change Service Name or Service Code.",
									})
								)
							}
							// first row errors (optional)
							errors.push(
								createError({
									path: `${path}[${firstServiceIndex}].service`,
									message:
										"Service Name already exists. Change Service Name or Service Code.",
								})
							)
							if (dup.code) {
								errors.push(
									createError({
										path: `${path}[${firstServiceIndex}].service_short_name`,
										message:
											"Service Code already exists. Change Service Name or Service Code.",
									})
								)
							}
							return
						}
					})

					if (errors.length > 0) {
						throw new Validator.ValidationError(errors)
					}

					return true
				}),
			trip_destinations:
				can_attach_services_to_destinations && type !== "EditSingleService"
					? Validator.array()
							.required("Please select a Destinations")
							.min(1, "Please select atleast one Destinations")
					: Validator.array().nullable(),
			quick_add: Validator.boolean(),
		})
	)
	const initialValues = useMemo<NewServiceCredentials>(() => {
		const InitialProp = propInitialValues || {}
		const { trip_destinations, ...restData } = InitialProp
		return {
			...INITIAL_VALUES,
			...propInitialValues,
			quick_add: !Object.values(restData || {}).length,
		}
	}, [propInitialValues])
	return (
		<Form<NewServiceCredentials>
			initialValues={initialValues}
			validate={validationSchema}
			onSubmit={withServerErrors(async (values) => {
				const {
					from,
					to,
					short_code,
					trip_destinations,
					quick_add,
					transport_service,
				} = values
				const services = transport_service?.map((s) => ({
					service: s.service,
					service_short_name: quick_add ? undefined : s.service_short_name,
					distance: s.distance || 0,
					start_time:
						s.start_time && !quick_add
							? formatDate(
									setUnit(clone(s.start_time), "seconds", 0),
									config.timeFormat
								)
							: undefined,
					duration: quick_add ? undefined : s.duration,
					service_details: s.service_details,
				}))

				const allServices =
					type === "EditSingleService" && services?.length
						? services[0]
						: { transport_service: services }
				const {
					data: { data: transportService },
				} = await xhr.post<{ data: ITransportService }>(url, {
					from: from?.name,
					to: to?.name,
					trip_destinations:
						trip_destinations?.length && can_attach_services_to_destinations
							? trip_destinations.map((t) => t.id)
							: [],
					short_code: short_code && !quick_add ? String(short_code) : undefined,
					service_location_id: serviceLocationId ?? undefined,
					...allServices,
				})
				onSuccess(transportService)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<GetFieldValue<boolean> name={`quick_add`}>
						{({ value: quickAdd }) => (
							<>
								{type === "EditSingleService" ? null : (
									<Grid>
										<Col sm={12} md={{ offset: 4, span: 8 }}>
											<Inline paddingBottom="2" justifyContent="end">
												<Box
													paddingX={"2"}
													paddingY={"1"}
													borderRadius="md"
													borderWidth={"2"}
													borderColor={"primary"}
													bgColor={quickAdd ? "primary" : "accent"}
													cursor={
														!quickAdd || submitting ? "disabled" : "pointer"
													}
												>
													<CheckboxInputField
														label={"Quick Add"}
														name={`quick_add`}
														disabled={!quickAdd || submitting}
													/>
												</Box>
											</Inline>
										</Col>
									</Grid>
								)}
								<Grid gap="4">
									<Col sm={12} md={4}>
										<Inline gap="2">
											<Box>
												<Icons.LocationMarker size="6" />
											</Box>
											<Stack>
												<Heading as="h3">Locations</Heading>
												<Text color="muted">
													Add pickup-drop location along with a short code for
													quick identification.
												</Text>
											</Stack>
										</Inline>
									</Col>
									{type === "addLocationService" ? (
										<Col>
											<Stack gap="4">
												<Grid gap="4">
													<Col>
														<SelectField
															select={SelectTransportServiceLocationPoints}
															label="From/Pickup/Start City"
															name="from"
															required
															multiple={false}
															creatable
														/>
													</Col>
													<Col>
														<SelectField
															select={SelectTransportServiceLocationPoints}
															label="To/Drop/End City"
															name="to"
															secondaryLabel="optional"
															multiple={false}
															creatable
														/>
													</Col>
												</Grid>
												<Grid gap="4">
													{quickAdd ? null : (
														<Col>
															<TextInputField
																type="text"
																label="Short Code"
																name="short_code"
																secondaryLabel="optional"
																placeholder="125"
															/>
														</Col>
													)}
													{can_attach_services_to_destinations ? (
														<Col>
															<SelectField
																select={SelectTripDestination}
																name="trip_destinations"
																label="Trip Destinations"
																multiple
																closeOnSelect
															/>
														</Col>
													) : null}
												</Grid>
											</Stack>
										</Col>
									) : (
										<Col>
											<Inline flexWrap={"wrap"} gap={"2"}>
												<Stack gap="1">
													<Box
														color="muted"
														fontSize="sm"
														letterSpacing="wider"
														fontWeight="semibold"
													>
														From/Pickup/Start City
													</Box>
													<Box fontSize="sm">{initialValues.from?.name}</Box>
												</Stack>
												<Divider sm />
												<Stack
													borderLeftWidth={"2"}
													borderLeftColor={"default"}
													paddingLeft={"2"}
													gap="1"
												>
													<Box
														color="muted"
														fontWeight="semibold"
														fontSize="sm"
														letterSpacing="wider"
													>
														To/Drop/End City
													</Box>
													<Box fontSize="sm">{initialValues.to?.name}</Box>
												</Stack>
												<Stack
													borderLeftWidth={"2"}
													borderLeftColor={"default"}
													paddingLeft={"2"}
													gap="1"
												>
													<Box
														color="muted"
														fontWeight="semibold"
														fontSize="sm"
														letterSpacing="wider"
													>
														Short Code
													</Box>
													<Box fontSize="sm">{initialValues.short_code}</Box>
												</Stack>
												<Stack
													borderLeftWidth={"2"}
													borderLeftColor={"default"}
													paddingLeft={"2"}
													gap="1"
												>
													<Box
														color="muted"
														fontWeight="semibold"
														fontSize="sm"
														letterSpacing="wider"
													>
														Trip Destination
													</Box>
													<Box fontSize="sm">
														{initialValues.trip_destinations?.length
															? initialValues.trip_destinations
																	?.map((td) => td.name)
																	.join(", ")
															: "All"}
													</Box>
												</Stack>
											</Inline>
										</Col>
									)}
								</Grid>
								<Divider sm />
								<Grid gap="4">
									<Col sm={12} md={4}>
										<Inline gap="2">
											<Box>
												<Icons.Tags size="6" />
											</Box>
											<Stack gap="1">
												<Heading as="h3">
													{canAddMoreServices
														? "Service and Itinerary Details"
														: "Service Details"}
												</Heading>
												<Text color="muted">
													{canAddMoreServices
														? "Please provide the service name with inclusions, distance, duration and itinerary description."
														: "Please provide the service name with inclusions distance and duration."}
												</Text>
											</Stack>
										</Inline>
									</Col>
									<Col>
										<FieldArray<
											Partial<ITransportService>
										> name="transport_service">
											{({ fields }) => {
												return (
													<>
														{fields.map((name, index) => (
															<Box display={"flex"} key={name}>
																{(fields?.length ?? 0) > 1 ? (
																	<Box
																		borderWidth={"1"}
																		borderColor={"muted"}
																		flex={"initial"}
																		width={"8"}
																		textAlign={"center"}
																		verticalAlign="middle"
																		paddingTop={"4"}
																		fontWeight="semibold"
																		bgColor={"inset"}
																	>
																		{index + 1}
																	</Box>
																) : null}
																<Stack
																	paddingTop={"2"}
																	gap={(fields?.length ?? 0) > 1 ? "4" : "0"}
																	flex={"1"}
																>
																	<Stack
																		gap={"6"}
																		paddingLeft={
																			(fields?.length ?? 0) > 1 ? "4" : "0"
																		}
																	>
																		<TextInputField
																			type="text"
																			label="Service Name"
																			name={`${name}.service`}
																			placeholder="Onway sightseeing"
																		/>
																		{quickAdd ? null : (
																			<>
																				<TextInputField
																					type="text"
																					label="Service Code Name"
																					name={`${name}.service_short_name`}
																					secondaryLabel="optional"
																					placeholder="e.g. SUPPLIER OR SPECIFIC HIDDEN DETAILS"
																					help="You can attach a code to the service for any unique and non customer sharable information."
																				/>

																				<Grid justifyContent={"evenly"} gap="4">
																					<Col>
																						<TextInputField
																							name={`${name}.distance`}
																							type="number"
																							label="Distance (kms)"
																							secondaryLabel="optional"
																							placeholder="420"
																						/>
																					</Col>
																					<Col>
																						<DatePickerField
																							label="Start Time"
																							name={`${name}.start_time`}
																							dateFormat={false}
																							timeFormat={
																								config.timeDisplayFormat
																							}
																							secondaryLabel="optional"
																							help="Default start time for the service"
																						/>
																					</Col>
																					<Col>
																						<TextInputField
																							name={`${name}.duration`}
																							type="number"
																							label="Duration (in mins)"
																							secondaryLabel="optional"
																							placeholder="60"
																						/>
																					</Col>
																				</Grid>
																			</>
																		)}
																		{canAddMoreServices ? (
																			<>
																				<MarkdownInputField
																					name={`${name}.service_details`}
																					label="Description"
																					placeholder="Upon your arrival at Railway Station, your driver will escort you to your hotel..."
																				/>

																				<Box>
																					{(fields?.length ?? 0) > 1 ? (
																						<Button
																							onClick={() =>
																								fields.remove(index)
																							}
																							title="Remove"
																							size="sm"
																							level="tertiary"
																						>
																							<Icons.Cancel /> Remove
																						</Button>
																					) : null}
																				</Box>
																			</>
																		) : null}
																	</Stack>
																	{canAddMoreServices ? (
																		<Divider
																			marginTop={"0"}
																			marginBottom={"0"}
																			sm
																		/>
																	) : null}
																</Stack>
															</Box>
														))}
														{canAddMoreServices ? (
															<Box
																paddingLeft={
																	(fields?.length ?? 0) > 1 ? "8" : "0"
																}
																marginTop={"4"}
															>
																<Button
																	onClick={() =>
																		fields.push({
																			service: "",
																			service_short_name: "",
																			distance: 0,
																			start_time: undefined,
																			duration: undefined,
																		})
																	}
																	level="secondary"
																	status="primary"
																	title="Add another service"
																>
																	<Icons.Plus /> Add another service
																	<Icons.NewFeature size={"10"} />
																</Button>
															</Box>
														) : null}
													</>
												)
											}}
										</FieldArray>
									</Col>
								</Grid>
								{canAddMoreServices ? null : (
									<>
										<Divider />
										<Grid gap="4">
											<Col sm={12} md={4}>
												<Inline gap="2">
													<Box>
														<Icons.Cog size="6" />
													</Box>
													<Stack gap="1">
														<Heading as="h3">Itinerary</Heading>
														<Text color="muted">
															You can (optionally) provide details for this
															service. This will allow autofilling day-wise
															schedule in an itinerary when this service is
															provided in a quote.
														</Text>
													</Stack>
												</Inline>
											</Col>
											<Col>
												<MarkdownInputField
													label="Description"
													name="transport_service[0].service_details"
													placeholder="Upon your arrival at Railway Station, your driver will escort you to your hotel..."
												/>
											</Col>
										</Grid>
									</>
								)}
								<Divider />

								<FormSpy<NewServiceCredentials>>
									{({ values }) => {
										const { short_code, from, to, transport_service } = values
										return transport_service?.map((service, ind) => (
											<Box
												padding="4"
												boxShadow="lg"
												rounded="lg"
												borderWidth="1"
												bgColor="subtle"
												marginY="4"
												key={service.service + ind}
											>
												<Box
													textTransform="uppercase"
													fontSize="sm"
													color="muted"
													marginBottom="4"
													fontWeight="semibold"
													letterSpacing="wider"
												>
													Summary
												</Box>
												<Box>
													{!from ? (
														<Heading color="muted">[Title]</Heading>
													) : (
														<Box>
															{short_code ? (
																<Box as="code" marginRight="1" display="block">
																	Code {short_code}
																</Box>
															) : null}
															<Heading>
																{from.name}
																{to ? (
																	<Box as="span" marginLeft="1">
																		to {to.name}
																	</Box>
																) : null}
																{service.service ? (
																	<Box as="span" marginLeft="1">
																		- {service.service}
																	</Box>
																) : null}
															</Heading>
														</Box>
													)}
												</Box>
												<Text>
													{joinAttributes(
														service.distance ? `${service.distance} kms` : null,
														service.start_time
															? formatDate(service.start_time, "HH:mm") + " hrs"
															: null,
														service.duration
															? `${humanizeDuration(
																	service.duration * 60 * 1000,
																	{
																		short: false,
																	}
																)}`
															: null
													)}
												</Text>
												<Box color="muted">
													{service.service_details ? (
														<Markdown>{service.service_details}</Markdown>
													) : (
														"[Itinerary]"
													)}
												</Box>
											</Box>
										))
									}}
								</FormSpy>
							</>
						)}
					</GetFieldValue>
					<Divider sm />
					<Grid>
						<Col sm={12} md={{ offset: 4, span: 8 }}>
							<Stack gap="4">
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting} size="lg">
										{submitting ? "Saving..." : "Save Service"}
									</Button>
									{onCancel ? (
										<Button
											onClick={onCancel}
											level="tertiary"
											size="lg"
											disabled={submitting}
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}

export function AddTransportServiceDialog({
	open,
	onClose,
	onSuccess,
	...props
}: Optional<
	Omit<React.ComponentProps<typeof NewServicesForm>, "onCancel">,
	"onSuccess"
> & {
	open: boolean
	onClose: () => void
}) {
	return (
		<Dialog open={open} onClose={onClose} lg>
			<Dialog.Header closeButton>
				<Dialog.Title>Add new Transport Service</Dialog.Title>
			</Dialog.Header>
			<Dialog.Body>
				<NewServicesForm
					onSuccess={(...args) => {
						onClose()
						onSuccess?.(...args)
					}}
					onCancel={onClose}
					{...props}
				/>
			</Dialog.Body>
		</Dialog>
	)
}
